import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { logEvent } from "@/lib/cloudServices";

interface Props {
  productName: string;
  query: string;
  decisionId?: string;
}

export default function PurchaseFeedback({ productName, query, decisionId }: Props) {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState<"ask" | "rating" | "done">("ask");
  const { user } = useAuth();

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible || step === "done") return null;

  const handleBought = (bought: boolean) => {
    if (user) {
      logEvent({ userId: user.id, decisionId, eventType: "purchase_feedback", metadata: { productName, query, bought } });
    }
    if (bought) {
      setStep("rating");
    } else {
      setStep("done");
    }
  };

  const handleRating = (rating: string) => {
    if (user) {
      logEvent({ userId: user.id, decisionId, eventType: "purchase_rating", metadata: { productName, query, rating } });
    }
    setStep("done");
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        className="mt-2 rounded-lg border border-border/30 bg-muted/15 px-3 py-2"
      >
        {step === "ask" && (
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground font-body">Did you buy this?</span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handleBought(true)}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-body text-score-high hover:bg-score-high/10 transition-colors"
              >
                <ThumbsUp className="w-2.5 h-2.5" />Yes
              </button>
              <button
                onClick={() => handleBought(false)}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-body text-muted-foreground hover:bg-muted/30 transition-colors"
              >
                <ThumbsDown className="w-2.5 h-2.5" />No
              </button>
            </div>
          </div>
        )}
        {step === "rating" && (
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5">How was it?</p>
            <div className="flex items-center gap-1.5">
              {[
                { label: "Great", value: "great", className: "text-score-high hover:bg-score-high/10" },
                { label: "Okay", value: "okay", className: "text-score-mid hover:bg-score-mid/10" },
                { label: "Regret", value: "regret", className: "text-score-low hover:bg-score-low/10" },
              ].map((r) => (
                <button
                  key={r.value}
                  onClick={() => handleRating(r.value)}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-body transition-colors ${r.className}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
